import { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Loader2, Landmark, CheckCircle2, ShieldCheck } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PaySetupPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try { setData((await axios.get(`${API}/pay-setup/${token}`)).data); }
    catch (e) { setError(e.response?.data?.detail || 'This link is invalid'); }
  }, [token]);
  useEffect(() => { load(); }, [load]);

  const connect = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/pay-setup/${token}/onboard`);
      window.location.href = r.data.onboarding_url;
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not start bank setup'); setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <Card className="w-full max-w-md" data-testid="pay-setup-card">
        <CardContent className="py-8 px-6 text-center space-y-4">
          {error ? (
            <p className="text-sm text-rose-400" data-testid="pay-setup-error">{error}</p>
          ) : !data ? (
            <Loader2 className="h-6 w-6 animate-spin mx-auto text-muted-foreground" />
          ) : data.payouts_enabled ? (
            <>
              <CheckCircle2 className="h-10 w-10 text-emerald-400 mx-auto" />
              <h1 className="text-xl font-bold">You're all set{data.name ? `, ${data.name}` : ''}!</h1>
              <p className="text-sm text-muted-foreground" data-testid="pay-setup-ready">Your bank is connected. Payments from event organizers land in your account in 1–2 business days — no fees.</p>
            </>
          ) : (
            <>
              <Landmark className="h-10 w-10 text-indigo-400 mx-auto" />
              <h1 className="text-xl font-bold">Get paid for your event work</h1>
              <p className="text-sm text-muted-foreground">
                {data.name ? `Hi ${data.name} — an` : 'An'} event organizer on Eventism wants to pay you directly to your bank.
                Connect once (about 2 minutes) and every future payment arrives automatically.
              </p>
              <Button className="w-full bg-indigo-600 hover:bg-indigo-700 text-white" disabled={busy} onClick={connect} data-testid="pay-setup-connect-btn">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : (data.connected ? 'Finish bank setup' : 'Connect my bank')}
              </Button>
              <p className="text-[11px] text-muted-foreground flex items-center justify-center gap-1">
                <ShieldCheck className="h-3 w-3" /> Secured by Stripe — Eventism never sees your bank login.
              </p>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
